import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Agentation } from 'agentation';
import { RouterProvider } from 'react-router-dom';
import type { ReactNode } from 'react';

import { queryClient } from '@shared/api/query-client';
import { PostHogProvider } from '@shared/analytics';
import { AuthProvider } from '@shared/auth';
import { LazyMotion, loadMotionFeatures } from '@shared/motion';
import { router } from './router';
import { env } from '../env';

/**
 * App-wide provider stack.
 *
 * Order matters:
 *   1. QueryClientProvider — TanStack Query must wrap anything that fetches.
 *   2. StubAuthProvider    — auth context. Replace with concrete provider later.
 *   3. LazyMotion          — loads Framer Motion features asynchronously so `m`
 *                            components work everywhere. `strict` warns if any
 *                            component accidentally uses the heavier `motion`
 *                            import (remove `strict` once full migration is done
 *                            if the warnings are distracting during incremental
 *                            migration).
 *   4. RouterProvider      — routes mount after auth context is available.
 *
 * Toast / Modal / FeatureFlag providers are intentionally absent — they get
 * added when those primitives are built. Don't pre-provide context for things
 * that don't exist yet.
 *
 * Dev-only tools (ReactQueryDevtools, Agentation) are gated behind `env.IS_DEV`
 * so they tree-shake out of the production bundle. The Agentation toolbar
 * (bottom-right) syncs annotations to a local agentation-mcp collector via the
 * `endpoint` prop. It is OFF unless `VITE_AGENTATION_ENDPOINT` is set — without
 * a configured (running) collector it would POST on every page load and spam
 * `ERR_CONNECTION_REFUSED`, so it's opt-in rather than on-by-default in dev.
 */
export function Providers({ children }: { children?: ReactNode }) {
  return (
    <PostHogProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LazyMotion features={loadMotionFeatures}>
            {children ?? <RouterProvider router={router} />}
          </LazyMotion>
          {env.IS_DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
          {env.IS_DEV && env.AGENTATION_ENDPOINT ? (
            <Agentation endpoint={env.AGENTATION_ENDPOINT} />
          ) : null}
        </AuthProvider>
      </QueryClientProvider>
    </PostHogProvider>
  );
}
